import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../constants/routes';
import { configurationService } from '../../services';
import { Language } from '../../constants/language';
import { CONTACT } from '../../models/contact/contact';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  
  // Helper function to determine if current language is RTL
  const isRTL = i18n.language === 'ar';

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    
    // Update i18n
    i18n.changeLanguage(newLang);
    
    // Update configuration service (this will also update storage)
    const currentConfig = configurationService.getConfiguration();
    configurationService.setConfiguration({
      ...currentConfig,
      language: newLang as Language
    });
    
    // Set document direction for RTL support
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  // Set initial direction
  React.useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  const footerLinks = {
    connect: [
      { label: t('footer.home', 'Home'), href: ROUTES.HOME },
      { label: t('footer.ministries', 'Ministries'), href: ROUTES.MINISTRIES },
      { label: t('footer.events', 'Events'), href: ROUTES.EVENTS },
      { label: t('footer.schools', 'Schools'), href: ROUTES.SCHOOLS },
      { label: t('footer.about', 'About'), href: ROUTES.ABOUT },
    ],
    resources: [
      { label: t('footer.store', 'Store'), href: ROUTES.STORE },
    ],
  };

  const socialLinks = [
    { 
      name: 'Facebook', 
      href: 'https://www.facebook.com/zoeegypt',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    },
    { 
      name: 'SoundCloud', 
      href: 'https://soundcloud.com/zoe-egypt',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7.2 2.2v10.7c-.6-.2-1.2-.3-1.8-.3-2 0-3.6 1.6-3.6 3.6s1.6 3.6 3.6 3.6c2 0 3.6-1.6 3.6-3.6V9.4l8.8-1.9v6.8c-.6-.2-1.2-.3-1.8-.3-2 0-3.6 1.6-3.6 3.6s1.6 3.6 3.6 3.6c2 0 3.6-1.6 3.6-3.6V0.8L7.2 2.2z"/>
        </svg>
      )
    },
    { 
      name: 'YouTube', 
      href: 'https://www.youtube.com/@ZoeForAllNations',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      )
    }
  ];

  return (
    <footer className="bg-black text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div className="md:col-span-2">
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3 mb-6`}>
              <div className="w-12 h-12 bg-white flex items-center justify-center">
                <span className="text-black text-2xl font-black">Z</span>
              </div>
              <span className="text-3xl font-black uppercase tracking-tight">ZOE</span>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6 max-w-md font-medium">
              {t('footer.description', 'Experiencing divine life through Christ\'s transformative power. From Egypt to the world, ZOE is shining, healing, and building a new generation.')}
            </p>
            <div className="space-y-3 text-gray-300">
              <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm">{CONTACT.INFO_EMAIL}</span>
              </div>
              <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-sm">{CONTACT.CONTACT_NUMBER}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6 text-white">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {footerLinks.connect.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold text-lg mb-6 text-white">{t('footer.resources')}</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section with Language Toggle */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* Social Media */}
            <div className="flex space-x-4 mb-6 md:mb-0">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-12 h-12 bg-gray-800 border-2 border-gray-700 flex items-center justify-center hover:bg-white hover:text-black hover:border-white transition-all duration-300 group"
                  aria-label={social.name}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="text-white group-hover:text-black transition-colors duration-300">
                    {social.icon}
                  </div>
                </a>
              ))}
            </div>

            {/* Language Switcher */}
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-4 mb-6 md:mb-0`}>
              <div className={`text-xs font-black uppercase tracking-widest text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`}>{t('footer.language')}:</div>
              <button 
                onClick={toggleLanguage}
                className="px-6 py-3 bg-white text-black hover:bg-gray-200 font-black text-sm uppercase tracking-wider transition-colors duration-300 border-2 border-white"
              >
                {i18n.language === 'en' ? 'العربية' : 'English'}
              </button>
            </div>

            {/* Copyright */}
            <div className="text-center md:text-right text-gray-400 text-sm">
              <p>© {currentYear} ZOE Ministry. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;